import {
    Avatar,
    Box,
    Button,
    Flex,
    Heading,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Spinner,
    Stack,
    Tag,
    Text,
    Textarea,
    useDisclosure,
    useToast,
    Divider,
    VStack
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import {Link, useParams} from "react-router-dom";
import {useSession} from "../../hooks/session";
import { useAuth } from "../../contexts/auth/AuthContext";
import {useEffect, useState} from "react";
import * as React from "react";
import {BsWhatsapp} from "react-icons/bs";
import {FaCopy, FaCheckCircle, FaExclamationTriangle} from "react-icons/fa";
import SessionCancelModal from "../../components/sessionCancel";
import {getSessionStatusDescription, SessionStatus} from "../../enum/session/SessionStatus";
import {format} from "date-fns";
import { ptBR } from 'date-fns/locale';
import api from "../../services/api";
import {CheckType} from "../../enum/session-check/CheckType";
import {phoneToWhatsApp} from "../../util/phone-number";
import copy from "copy-to-clipboard";
import { Rating } from '@smastrom/react-rating';
import '@smastrom/react-rating/style.css';
import Footer from "../../components/Footer";
import { getPropertyCategoryDescription } from "../../enum/PropertyCategory";

const DeliverableView = () => {
    const {id} = useParams();
    const { user } = useAuth();
    const sessionRes = useSession(id);
    const session = sessionRes?.data;
    const isLoading = !sessionRes;
    const toast = useToast();

    const {isOpen: isCheckOpen, onOpen: onCheckOpen, onClose: onCheckClose} = useDisclosure();
    const {isOpen: isBriefingOpen, onOpen: onBriefingOpen, onClose: onBriefingClose} = useDisclosure();

    const [notes, setNotes] = useState("");
    const [lacking_areas, setLacking_areas] = useState("");
    const [viewedBriefingIds, setViewedBriefingIds] = useState<string[]>([]);
    const [rating, setRating] = useState(0);

    const [briefingModalData, setBriefingModalData] = useState<{
        serviceTitle: string;
        subTitle: string;
        content: string;
        packOrientation: string | null;
        type: string;
        id: number | null;
    }>({
        serviceTitle: "",
        subTitle: "",
        content: "",
        packOrientation: null,
        type: "",
        id: null
    });

    useEffect(() => {
        if (sessionRes?.status && sessionRes.status !== 200) {
            toast({
                title: "Erro ao carregar dados da sessão.",
                description: `Status: ${sessionRes.status}`,
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top"
            });
        }
    }, [sessionRes, toast]);

    const isViewed = (data: any, type: 'pre' | 'pos') => {
        if (!data) return false;
        if (data.is_viewed) return true;
        
        const key = data.pack_orientation
            ? `s:${data.session_item_id}:${type}`
            : (data.briefing_id 
                ? `b:${data.briefing_id}` 
                : (data.item_id ? `i:${data.item_id}:${type}` : `s:${data.session_item_id}:${type}`));
                
        return viewedBriefingIds.includes(key);
    };

    const openBriefing = (briefing: any, type: 'pre' | 'pos') => {
        const data = type === 'pre' ? briefing.pre : briefing.pos;
        if (!data) return;

        setBriefingModalData({
            serviceTitle: briefing.title,
            subTitle: type === 'pre' ? "Orientação Pré-Produção" : "Orientação Pós-Produção",
            content: data.content || "",
            packOrientation: data.pack_orientation || null,
            type: data.type || "Standard",
            id: data.briefing_id || null,
        });
        
        onBriefingOpen();

        const viewKey = data.pack_orientation
            ? `s:${data.session_item_id}:${type}`
            : (data.briefing_id 
                ? `b:${data.briefing_id}` 
                : (data.item_id ? `i:${data.item_id}:${type}` : `s:${data.session_item_id}:${type}`));

        const isMarkedViewed = viewedBriefingIds.includes(viewKey);

        if (!data.is_viewed && !isMarkedViewed) {
            const payload: any = {};
            
            if (data.pack_orientation) {
                payload.session_item_id = data.session_item_id;
                payload.type = type;
            } else if (data.briefing_id) {
                payload.briefing_id = data.briefing_id;
            } else if (data.item_id) {
                payload.item_id = data.item_id;
                payload.type = type;
            } else if (data.session_item_id) {
                payload.session_item_id = data.session_item_id;
                payload.type = type;
            }
            
            if (payload.briefing_id || payload.item_id || payload.session_item_id) {
                api.post(`/provider/briefing-view`, payload)
                   .then((res) => {
                       const seenKeys = res.data?.data?.viewed_keys || [];
                       if (seenKeys.length > 0) {
                           setViewedBriefingIds(prev => {
                               const combined = [...prev, ...seenKeys];
                               return Array.from(new Set(combined));
                           });
                       } else {
                           setViewedBriefingIds(prev => [...prev, viewKey]);
                       }
                   })
                   .catch(err => console.error("Erro ao registrar visualização", err));
            }
        }
    };

    const handleCheckIn = () => {
        api.post(`/provider/session-reports-start`, { id: session?.id })
            .then((response) => {
                if (response.status === 201) {
                    window.location.reload();
                }
            })
            .catch(() => alert("Falha ao salvar check-in"));
    };

    const handleCheckOut = () => {
        api.post(`/provider/session-reports`, {
            session: {
                status: SessionStatus.PENDING,
                id: session?.id,
            },
            additional_info: { notes, lacking_areas, rating },
            check_type: CheckType.END_PROVIDER_SESSION,
        })
            .then((response) => {
                if (response.status === 201) {
                    window.location.reload();
                }
            })
            .catch(() => alert("Falha ao salvar check-out"));
    };

    const copyToClipboard = (text: string) => {
        copy(text);
        toast({
            title: "Copiado para a área de transferência",
            status: "success",
            duration: 2000,
            isClosable: true,
            position: "top"
        });
    };

    if (isLoading) {
        return (
            <Flex justify="center" align="center" h="100vh" direction="column" gap="4" bg="white">
                <Spinner color="#2196F3" size="xl" />
                <Text color="slate.500" fontWeight="500" fontFamily="Inter">Carregando...</Text>
            </Flex>
        );
    }

    const whatsAppInRoute = phoneToWhatsApp(
        String(session?.scheduling_responsible_info_phone),
        `Olá ${session?.scheduling_responsible_info}, Já estou a caminho do imóvel. Até logo!`
    );
    const whatsAppContact = phoneToWhatsApp(
        String(session?.user?.phone),
        `Olá ${session?.user?.name}!`
    );

    return (
        <Box className="bg-[#FFFFFF] text-slate-800 min-h-screen pb-24 font-inter">
            <header className="sticky top-0 z-50 bg-white px-4 py-4 border-b border-slate-100 flex items-center justify-between shadow-sm">
                <Flex align="center" gap="3">
                    
                    <Flex align="center" gap="2">
                        <Avatar 
                            src={session?.client?.logo} 
                            name={session?.client?.name} 
                            size="md" 
                            borderRadius="lg"
                        />
                        <Text fontWeight="700" fontSize="18px" color="#475569" letterSpacing="-0.5px">
                            {session?.client?.name || 'Cliente'}

                        </Text>
                    </Flex>
                </Flex>
                <div className="bg-[var(--ex-secondary)] text-white px-5 py-1.5 rounded-full text-[14px] font-bold shadow-md shadow-blue-500/10">
                    {getSessionStatusDescription(Number(session?.status))}
                </div>
            </header>

            <main className="max-w-md mx-auto p-4 space-y-6">
                <section className="space-y-1 px-1">
                   <div className="text-[14px] text-slate-500 font-medium">
                        ID {session?.id} &bull; Referência {session?.session_vertical_data_reference || '-'}
                    </div>
                    <div className="font-bold text-[16px] text-[#475569] tracking-[-0.5px]">
                        {session?.scheduled_for && format(new Date(session.scheduled_for), "HH:mm")}
                        <span className="text-[#475569] mx-1">|</span>
                        {session?.scheduled_for && (() => {
                            const formattedDate = format(new Date(session.scheduled_for), "dd/MM - EEEE", { locale: ptBR });
                            return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
                        })()}
                    </div>
                </section>

                <div className="space-y-4">
                    {session?.briefings && session.briefings.length > 0 ? (
                        session.briefings
                            .filter((briefing: any) => !briefing.provider_id || Number(briefing.provider_id) === Number(user?.associable_id))
                            .map((briefing: any, index: number) => {
                            const hasPre = !!briefing.pre?.content;
                            const hasPos = !!briefing.pos?.content;

                            if (!hasPre && !hasPos) return null;

                            return (
                                <div key={index} className="bg-[#F1F5F9] p-5 rounded-lg border border-slate-200 space-y-3">
                                    <div className="space-y-0.5">
                                        <h2 className="font-bold text-[16px] text-[#475569] tracking-[-0.5px]">{briefing.title}</h2>
                                        <p className="text-[11px] text-slate-500 font-medium">
                                            Orientação {briefing.pre?.type === 'Personalizada' || briefing.pos?.type === 'Personalizada' ? 'Personalizada' : 'Padrão'}
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        {hasPre && (
                                            <div className="space-y-2 text-center">
                                                <button 
                                                    onClick={() => openBriefing(briefing, 'pre')}
                                                    className="w-full bg-[var(--ex-secondary)] text-white py-3 rounded-lg font-bold text-sm active:opacity-80 transition-opacity"
                                                >
                                                    Pré-Produção
                                                </button>
                                                <Text fontSize="xs" color="slate.400"></Text>
                                                <p className={`text-[12px] font-bold ${isViewed(briefing.pre, 'pre') ? 'text-slate-400' : 'text-red-500'}`}>
                                                    {isViewed(briefing.pre, 'pre') 
                                                        ? `Visto${briefing.pre.viewed_at ? ' em ' + briefing.pre.viewed_at : ''}` 
                                                        : 'Não visualizado'}
                                                </p>
                                            </div>
                                        )}
                                        {hasPos && (
                                            <div className="space-y-2 text-center">
                                                <button 
                                                    onClick={() => openBriefing(briefing, 'pos')}
                                                    className="w-full bg-[var(--ex-secondary)] text-white py-3 rounded-lg font-bold text-sm active:opacity-80 transition-opacity"
                                                >
                                                    Pós-Produção
                                                </button>
                                                <p className={`text-[12px] font-bold ${isViewed(briefing.pos, 'pos') ? 'text-slate-400' : 'text-red-500'}`}>
                                                    {isViewed(briefing.pos, 'pos') 
                                                        ? `Visto${briefing.pos.viewed_at ? ' em ' + briefing.pos.viewed_at : ''}` 
                                                        : 'Não visualizado'}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="bg-[#F1F5F9] p-5 rounded-lg border border-slate-200">
                            <h2 className="text-lg font-bold mb-1 text-[#1E293B]">Briefing de Produção</h2>
                            <p className="text-sm text-slate-500 font-medium">Nenhuma orientação técnica disponível para este ensaio.</p>
                        </div>
                    )}
                </div>


                <section className="space-y-3 px-1">
                    {session?.session_category && getPropertyCategoryDescription(session.session_category) !== '' && (
                        <div className="flex items-center gap-3">
                            <div className="w-[18px] h-[18px] bg-green-400 rounded-full flex items-center justify-center">
                                <CheckIcon color="white" w={3} h={3} />
                            </div>
                            <span className="text-[14px] text-slate-500 font-medium">
                                Negócio: {getPropertyCategoryDescription(session.session_category)}
                            </span>
                        </div>
                    )}
                    <div className="flex items-center gap-3">
                        <div className={`w-[18px] h-[18px] ${session?.session_vertical_data_with_condo ? 'bg-green-400' : 'bg-gray-400'} rounded-full flex items-center justify-center`}>
                            {session?.session_vertical_data_with_condo ? (
                                <CheckIcon color="white" w={3} h={3} />
                            ) : (
                                <Text color="white" fontSize="10px" fontWeight="black">X</Text>
                            )}
                        </div>
                        <span className="text-[14px] text-slate-500 font-medium">
                            {session?.session_vertical_data_with_condo ? 'Captar imagens do condomínio' : 'Não captar imagens do condomínio'}
                        </span>
                    </div>
                </section>

                <section className="space-y-4">
                    <div className="px-1">
                        <h3 className="font-bold text-[18px] text-[#475569] tracking-[-0.5px]">Logística</h3>
                        <p className="text-[14px] text-slate-500 font-medium">{session?.provider_access_text || 'Alguém irá acompanhar o fotógrafo'}</p>
                    </div>
                    <div className="bg-[#F1F5F9] p-6 rounded-lg space-y-4">
                        <div className="space-y-2 font-bold text-[#475569]">
                            <p className="text-[15px]"><span className="text-[#475569]">Anfitrião:</span> <span className="text-[14px] text-slate-500 font-medium">Falar com {session?.scheduling_responsible_info || 'N/A'}</span></p>
                            <p className="text-[15px]"><span className="text-[#475569]">Função:</span> <span className="text-[14px] text-slate-500 font-medium">{session?.scheduling_responsible_info_role || 'Proprietário'}</span></p>
                            <div className="">
                                <p className="text-[15px] text-[#475569]">Outras informações ou observações:</p>
                                <p className="text-[14px] text-slate-500 font-medium leading-relaxed">
                                    "{session?.scheduling_responsible_info_extra || 'Sem observações extras.'}"
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={() => window.open(whatsAppInRoute, '_blank')}
                            className="w-full bg-[var(--ex-primary)] text-slate-900 py-4 rounded-lg font-bold text-[15px] shadow-sm active:scale-[0.99] transition-all"
                        >
                            Entrar em contato com o anfitrião
                        </button>
                    </div>
                </section>

                {session?.key_pickup_address && (
                    <div className="bg-[var(--ex-secondary)] text-white p-6 rounded-lg shadow-xl relative mt-4 space-y-5">
                        <div className="flex justify-between items-start">
                            <div className="space-y-1">
                                <h3 className="text-lg font-bold">Coleta de Chaves</h3>
                                <p className="text-[16px] font-bold leading-tight">{session.key_pickup_address}</p>
                                <p className="text-sm opacity-90 font-medium">
                                    {session.key_pickup_bairro || session.address_bairro}, {session.key_pickup_cidade || session.address_cidade}
                                </p>
                                {(session.key_pickup_unit || session.key_pickup_complement) && (
                                    <p className="text-sm opacity-90 font-medium">
                                        {session.key_pickup_unit ? `Unidade ${session.key_pickup_unit}` : ''}
                                        {session.key_pickup_complement ? (session.key_pickup_unit ? ` - ${session.key_pickup_complement}` : session.key_pickup_complement) : ''}
                                    </p>
                                )}
                            </div>
                            <IconButton
                                aria-label="Copiar"
                                icon={<FaCopy />}
                                variant="ghost"
                                color="white"
                                _hover={{ bg: "white/20" }}
                                onClick={() => copyToClipboard(session.key_pickup_address)}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3 pb-2">
                            <button 
                                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(session.key_pickup_address)}`, '_blank')}
                                className="flex items-center justify-center gap-2 bg-white text-[var(--ex-secondary)] py-3 rounded-lg font-black text-xs shadow-md"
                            >
                                <img alt="Google Maps" className="w-4 h-4" src="/icons/google-maps.png"/>
                                Google Maps
                            </button>
                            <button 
                                onClick={() => window.open(`https://waze.com/ul?q=${encodeURIComponent(session.key_pickup_address)}`, '_blank')}
                                className="flex items-center justify-center gap-2 bg-white text-[var(--ex-secondary)] py-3 rounded-lg font-black text-xs shadow-md"
                            >
                                <img alt="Waze" className="w-4 h-4" src="/icons/waze.png"/>
                                Waze
                            </button>
                        </div>
                    </div>
                )}

                <section className="space-y-4">
                    <h3 className="text-[19px] font-bold text-[#475569] tracking-[-0.5px] px-1">Endereço da produção</h3>
                    <div className="bg-[#F1F5F9] p-6 rounded-lg space-y-5 border border-slate-200">
                        {(() => {
                            const productionAddress = `${session?.address_rua || ''}, ${session?.address_numero || ''} - ${session?.address_bairro || ''}, ${session?.address_cidade || ''} - ${session?.address_estado || ''}`;
                            
                            return (
                                <>
                                    <div className="flex justify-between items-start">
                                        <div className="space-y-1 font-bold">
                                            <p className="text-[16px] text-[#475569] font-medium">
                                                {`${session?.address_rua || 'Rua não informada'}, ${session?.address_numero || ''}`}
                                            </p>
                                            <p className="text-[16px] text-[#475569] font-medium">{session?.address_bairro}, {session?.address_cidade}</p>
                                            {(session?.session_vertical_data_unit || session?.session_vertical_data_complement) && (
                                                <p className="text-[16px] text-[#475569] font-medium">
                                                    {session?.session_vertical_data_unit ? `Unidade ${session.session_vertical_data_unit}` : ''} 
                                                    {session?.session_vertical_data_complement ? ` - ${session.session_vertical_data_complement}` : ''}
                                                </p>
                                            )}
                                            <p className="text-[14px] text-slate-500 font-medium">Área (m2): <span className="text-[16px] text-[#475569] font-medium">{session?.session_vertical_data_area || '-'} m<sup>2</sup></span></p>
                                        </div>
                                        <IconButton
                                            aria-label="Copiar"
                                            icon={<FaCopy />}
                                            variant="ghost"
                                            color="gray.600"
                                            _hover={{ bg: "slate.200" }}
                                            onClick={() => copyToClipboard(productionAddress)}
                                        />
                                    </div>
                                    <div className="space-y-4">
                                        <p className="text-[14px] text-slate-500 font-medium">Ao chegar no Imóvel faça o check-in:</p>
                                        {(!session?.SessionCheckStartProvider && [String(SessionStatus.SCHEDULED), String(SessionStatus.CONFIRMED)].includes(String(session?.status))) ? (
                                            <button 
                                                onClick={onCheckOpen}
                                                className="w-full bg-[var(--ex-primary)] text-slate-900 py-4 rounded-lg font-bold text-[16px] shadow-sm active:scale-[0.99] transition-all"
                                            >
                                                Fazer o check-in
                                            </button>
                                        ) : session?.SessionCheckStartProvider ? (
                                            <Flex gap={4} mt={2} mb={6} justify="center">
                                                <Box>
                                                    <Text fontSize="xs" color="slate.400" fontWeight="400">
                                                        Check-in realizado com sucesso {session?.SessionCheckStartProvider}
                                                    </Text>
                                                </Box>
                                            </Flex>
                                        ) : null}
                                        <div className="grid grid-cols-2 gap-3">
                                            <button 
                                                onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(productionAddress)}`, '_blank')}
                                                className="flex items-center justify-center gap-2 bg-white border border-slate-200 text-[var(--ex-secondary)] py-3 rounded-lg font-black text-xs shadow-sm"
                                            >
                                                <img alt="Google Maps" className="w-4 h-4" src="/icons/google-maps.png"/>
                                                Google Maps
                                            </button>
                                            <button 
                                                onClick={() => window.open(`https://waze.com/ul?q=${encodeURIComponent(productionAddress)}`, '_blank')}
                                                className="flex items-center justify-center gap-2 bg-white border border-slate-200 text-[var(--ex-secondary)] py-3 rounded-lg font-black text-xs shadow-sm"
                                            >
                                                <img alt="Waze" className="w-4 h-4" src="/icons/waze.png"/>
                                                Waze
                                            </button>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </section>

                <button 
                    onClick={() => window.open(whatsAppInRoute, '_blank')}
                    className="w-full bg-[var(--ex-secondary)] text-white py-4 rounded-lg font-bold text-[17px] shadow-xl shadow-blue-500/20 flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                >
                    Avisar que estou a caminho &gt;&gt;
                </button>

                <footer className="pt-8 pb-10">
                    <Flex gap={4} mt={2} mb={6} justify="center">
                         <Box>
                             <Text fontSize="xs" color="slate.400">
                                Agendado em {session?.created_at && format(new Date(session.created_at), "dd/MM/yyyy HH:mm")} por {session?.user?.name}
                             </Text>
                         </Box>
                    </Flex>
                    
                    <VStack spacing={3} align="stretch">
                        <Box>
                            <button 
                                onClick={() => window.open(whatsAppContact, '_blank')}
                                className="w-full bg-[#F1F5F9] text-slate-900 py-4 rounded-lg font-bold text-[15px] border border-slate-200"
                            >
                                Entrar em contato com o agendador
                            </button>
                        </Box>
                        <Box>
                            <SessionCancelModal
                                id={String(session?.id)}
                                variantButton="unstyled"
                                status={String(SessionStatus.CANCELED_CHARGED)}
                                buttonText={<div className="w-full bg-[#1E293B] text-white py-4 rounded-lg font-bold text-[15px] block cursor-pointer text-center active:bg-slate-900 transition-colors">Cancelar Produção</div>}
                            />
                        </Box>
                        <Box>
                            <button 
                                onClick={onCheckOpen} 
                                className="w-full bg-[#F1F5F9] mt-3 text-slate-900 py-4 rounded-lg font-bold text-[15px] border border-slate-200"
                            >
                                Reportar Alterações
                            </button>
                        </Box>    
                    </VStack>
                    <Flex justify="center" mt={4}>
                         <Flex gap={2} p={3} alignItems={'center'} bg={'green.200'} w={'100%'} flexDirection={'column'}
                          borderRadius={'lg'}>
                        <Text w={'250px'} fontWeight={'500'} fontSize={'16px'} color={'gray.500'}>Para fazer a entrega da
                            produção acesse o Studio Piperz na versão desktop.</Text>
                        <Button w={'120px'} color={'white'} bg={'#4D5562'} onClick={() => copyToClipboard('https://studio.piperz.io/login')}>Copiar link</Button>
                    </Flex>
                    </Flex>
                </footer>
            </main>

            <Modal isOpen={isBriefingOpen} onClose={onBriefingClose} scrollBehavior={'inside'} size={'full'}>
                <ModalOverlay />
                <ModalContent bg="white" color="#1E293B">
            
            <ModalCloseButton />
            <ModalBody pb="8">
                <Stack spacing="5">
                    {briefingModalData.packOrientation && briefingModalData.content && (
                        <ModalHeader 
                            color="#475569" 
                            fontWeight="bold" 
                            fontSize="18px" 
                            textAlign="center" 
                            pt="6"
                        >
                            Orientação específica do Pack de Serviços
                        </ModalHeader>
                    )}

                    {briefingModalData.packOrientation && (
                        <Box p="4"  bg="blue.50" borderRadius="lg" border="1px solid" borderColor="blue.100">
                            <Text fontWeight="700" mb={1} fontSize="11px" color="blue.600" textTransform="uppercase">ORIENTAÇÃO DO PACK DE SERVIÇOS</Text>
                            <Box 
                                fontSize="14px" 
                                fontWeight="500" 
                                color="blue.800" 
                                lineHeight="relaxed"
                                dangerouslySetInnerHTML={{ __html: `Orientação específica de ${briefingModalData.packOrientation}` }}
                            />
                        </Box>
                    )}
                    <ModalHeader 
                color="#475569" 
                fontWeight="bold" 
                fontSize="18px" 
                textAlign="center" 
                pt="6"
            >
                {briefingModalData.serviceTitle}
            </ModalHeader>
                    

                    {briefingModalData.content && (
                        <Box p="6" bg="#F1F5F9" borderRadius="lg" border="1px solid" borderColor="slate.200">
                            <Text fontWeight="700" mb={2} fontSize="11px" color="slate.500" textTransform="uppercase">{briefingModalData.subTitle}</Text>
                            <Box 
                                fontSize="15px" 
                                fontWeight="500" 
                                color="#475569" 
                                lineHeight="relaxed"
                                dangerouslySetInnerHTML={{ __html: briefingModalData.content }}
                                sx={{
                                    'p': { marginBottom: '1rem' },
                                    'p:last-child': { marginBottom: 0 },
                                    'strong': { fontWeight: '700' }
                                }}
                            />
                        </Box>
                    )}

                </Stack>
            </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="slate-100" p={6}>
                        <Button bg="var(--ex-secondary)" color="white" w={'220px'} mx={'auto'} py="4" borderRadius="lg" fontSize="md" fontWeight="600" _hover={{ bg: "var(--ex-secondary)", opacity: 0.9 }} onClick={onBriefingClose}>Entendido</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isCheckOpen} size="xs" onClose={onCheckClose}>
                <ModalOverlay />
                <ModalContent bg="white" color="#1E293B" borderRadius="3xl" p={2}>
                    <ModalHeader fontWeight="900" fontSize="xl">
                        {!session?.SessionCheckStartProvider ? 'Confirmar Check-in' : 'Confirmar Check-out'}
                    </ModalHeader>
                    <ModalCloseButton mt={2} />
                    <ModalBody>
                        {!session?.SessionCheckStartProvider ? (
                            <Text fontWeight="bold" fontSize="xs">Podemos confirmar o check-in?</Text>
                        ) : (
                            <Stack spacing="5">
                                <Box>
                                    <Text mb="2" fontSize="xs" fontWeight="900" color="slate.400" textTransform="uppercase">FALTOU ALGO?</Text>
                                    <Textarea 
                                        placeholder="Ex: Não foi possível captar a varanda..." 
                                        bg="#F1F5F9"
                                        border="1px solid"
                                        borderColor="slate-200"
                                        borderRadius="2xl"
                                        p={4}
                                        fontWeight="600"
                                        onChange={(e) => setLacking_areas(e.target.value)} 
                                    />
                                </Box>
                                <Box>
                                    <Text mb="2" fontSize="xs" fontWeight="900" color="slate.400" textTransform="uppercase">OBSERVAÇÕES</Text>
                                    <Textarea 
                                        placeholder="Descreva aqui se houve algum problema." 
                                        bg="#F1F5F9"
                                        border="1px solid"
                                        borderColor="slate-200"
                                        borderRadius="2xl"
                                        p={4}
                                        fontWeight="600"
                                        onChange={(e) => setNotes(e.target.value)} 
                                    />
                                </Box>
                                <Box textAlign="center" pt={4}>
                                    <Text mb="3" fontSize="xs" fontWeight="900" color="slate.400" textTransform="uppercase">AVALIE A PRODUÇÃO</Text>
                                    <Flex justify="center" bg="#F1F5F9" py={4} borderRadius="2xl">
                                        <Rating style={{ maxWidth: 140 }} value={rating} onChange={setRating} />
                                    </Flex>
                                </Box>
                            </Stack>
                        )}
                    </ModalBody>
                    <ModalFooter gap={3} pb={6}>
                        <Button variant="ghost" onClick={onCheckClose} fontWeight="bold" color="slate.400">Sair</Button>
                        <Button 
                            bg="var(--ex-secondary)" color="white" borderRadius="xl" px={8} py={6} fontWeight="900"
                            onClick={!session?.SessionCheckStartProvider ? handleCheckIn : handleCheckOut}
                        >
                            Confirmar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Footer/>
        </Box>
    );
};


export default DeliverableView;